<template>
  <div>
    <div class="mb-4 flex justify-content-between align-items-center">
      <span class="pr-2 mr-2 font-bold text-2xl">Setting Aplikasi</span>
      <div>
        <i class="pi pi-home mr-2"></i>
        <span class="font-small">/ Master / Setting Aplikasi</span>
      </div>
    </div>
    <div class="grid">
      <div class="col-12 lg:col-8 lg:col-offset-2">
        <div class="card bg-white">
          <DataTable
            :value="items"
            :loading="isLoadingItems"
            :row-hover="true"
            class="p-datatable-sm"
            @request="onRequestData"
          >
            <Column field="key" headerClass="hidden">
              <template #body="{ data }">
                <div class="font-bold">{{ data.key.toUpperCase() }}</div>
                <br />
                <div v-if="data.desc" class="font-italic text-500">
                  {{ data.desc }}
                </div>
              </template>
            </Column>
            <Column field="value" headerClass="hidden" style="width: 40%">
              <template #body="{ data }">
                <div v-if="data.type === 'boolean'">
                  <div v-if="data.value === '1'">Ya</div>
                  <div v-else>Tidak</div>
                </div>
                <div v-else-if="data.type === 'datetime'">
                  {{ formatDate(data.value, true) }}
                </div>
                <div v-else>{{ data.value }}</div>
              </template>
            </Column>
            <Column headerClass="hidden">
              <template #body="{ data }">
                <Button
                  type="button"
                  icon="pi pi-pencil"
                  class="
                    p-button-outlined p-button-secondary p-button-text
                    mr-2
                  "
                  @click="onEditData(data)"
                />
              </template>
            </Column>
          </DataTable>
        </div>
      </div>

      <div class="col-12 lg:col-8 lg:col-offset-2">
        <div class="card bg-white">
          <div class="flex justify-content-between align-items-center mb-4">
            <h5 class="mb-0">Database Sync Config</h5>
            <div class="flex gap-2 justify-content-end">
              <Button
                :label="isDbFormActive ? 'Cancel' : 'Update Config'"
                :severity="isDbFormActive ? 'success' : 'danger'"
                :icon="isDbFormActive ? 'pi pi-times' : 'pi pi-pencil'"
                @click="toggleDbFormActive"
              />
              <Button
                label="Save"
                icon="pi pi-check"
                :loading="isLoadingDb"
                :disabled="!isDbFormActive"
                class="ml-2 p-button-sm"
                @click="saveDbConfig"
              />
            </div>
          </div>

          <div class="formgrid grid">
            <div class="field col-12 md:col-6">
              <label>Host</label>
              <InputText
                v-model="dbForm.DB_HOST"
                class="w-full"
                :disabled="!isDbFormActive"
                @focus="onDbFormFocus"
                @blur="onDbFormBlur"
              />
            </div>

            <div class="field col-12 md:col-6">
              <label>Port</label>
              <InputNumber
                v-model="dbForm.DB_PORT"
                class="w-full"
                :useGrouping="false"
                :disabled="!isDbFormActive"
                @focus="onDbFormFocus"
                @blur="onDbFormBlur"
              />
            </div>

            <div class="field col-12 md:col-6">
              <label>Database Name</label>
              <InputText
                v-model="dbForm.DB_NAME"
                class="w-full"
                :disabled="!isDbFormActive"
                @focus="onDbFormFocus"
                @blur="onDbFormBlur"
              />
            </div>

            <div class="field col-12 md:col-6">
              <label>User</label>
              <InputText
                v-model="dbForm.DB_USER"
                class="w-full"
                :disabled="!isDbFormActive"
                @focus="onDbFormFocus"
                @blur="onDbFormBlur"
              />
            </div>

            <div class="field col-12">
              <label>Password</label>
              <Password
                v-model="dbForm.DB_PASSWORD"
                toggleMask
                :feedback="false"
                class="w-full"
                input-class="w-full"
                :disabled="!isDbFormActive"
                @focus="onDbFormFocus"
                @blur="onDbFormBlur"
              />
            </div>

            <div class="field col-12">
              <div class="flex gap-2 justify-content-end">
                <Button
                  label="Test Connection"
                  icon="pi pi-link"
                  severity="info"
                  outlined
                  :loading="isTestingDb"
                  @click="testDbConfig"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- AUTO SCHEDULER -->
      <div class="col-12 lg:col-4 lg:col-offset-2">
        <div class="card bg-white h-full">
          <div class="flex justify-content-between align-items-center mb-4">
            <div>
              <div class="text-xl font-bold">Auto Scheduler</div>
              <div class="text-500 text-sm">
                Sinkronisasi otomatis transaksi
              </div>
            </div>

            <Tag
              :severity="autoScheduler.auto?.enabled ? 'success' : 'danger'"
              :value="autoScheduler.auto?.enabled ? 'Enabled' : 'Disabled'"
            />
          </div>

          <div class="field mb-4">
            <div class="flex gap-2 justify-content-end">
              <label class="block mb-2">Enable Auto Sync</label>
            </div>
            <div class="flex gap-2 justify-content-end">
              <InputSwitch v-model="autoForm.enabled" />
            </div>
          </div>

          <Divider />

          <div class="mb-3">
            <div class="text-500 text-sm">Run Time</div>
            <div class="font-bold">
              {{ autoScheduler.auto?.run_time || '-' }}
            </div>
          </div>

          <div class="mb-3">
            <div class="text-500 text-sm">Last Auto Run</div>
            <div class="font-bold">
              {{
                autoScheduler.auto?.last_auto_run
                  ? formatDate(autoScheduler.auto.last_auto_run, false)
                  : '-'
              }}
            </div>
          </div>

          <div class="mb-4">
            <div class="text-500 text-sm">Last Successful Sync</div>
            <div class="font-bold">
              {{
                autoScheduler.last_sync
                  ? formatDate(autoScheduler.last_sync, true)
                  : '-'
              }}
            </div>
          </div>

          <Divider />

          <div class="field mb-4">
            <label class="block mb-2">Run Time</label>

            <Calendar
              v-model="autoForm.run_time"
              timeOnly
              hourFormat="24"
              class="w-full"
            />
          </div>

          <div class="field mb-4">
            <label class="block mb-2">Max Sync</label>

            <InputNumber
              v-model="autoForm.limit"
              class="w-full"
              :useGrouping="false"
              :min="1"
              :max="100000"
              :allowEmpty="false"
            />
          </div>

          <Button
            label="Save Auto Scheduler"
            icon="pi pi-save"
            class="w-full"
            :loading="isLoadingAuto"
            @click="saveAutoScheduler"
          />
        </div>
      </div>

      <!-- MANUAL SYNC -->
      <div class="col-12 lg:col-4">
        <div class="card bg-white h-full">
          <div class="flex justify-content-between align-items-center mb-4">
            <div>
              <div class="text-xl font-bold">Manual Sync</div>
              <div class="text-500 text-sm">Jalankan sinkronisasi manual</div>
            </div>

            <Tag
              :severity="manualScheduler.is_running ? 'warning' : 'success'"
              :value="manualScheduler.is_running ? 'Running' : 'Idle'"
            />
          </div>

          <div class="mb-3">
            <div class="text-500 text-sm">Progress</div>

            <ProgressBar
              :value="manualScheduler.progress?.percent || 0"
              class="mb-2"
            />

            <div class="text-sm">
              {{ manualScheduler.progress?.current || 0 }}
              /
              {{ manualScheduler.progress?.total || 0 }}
            </div>
          </div>

          <div class="mb-3">
            <div class="text-500 text-sm">Current Transaction</div>
            <div class="font-bold">
              {{ manualScheduler.progress?.current_trx || '-' }}
            </div>
          </div>

          <div class="mb-4">
            <div class="text-500 text-sm">Last Sync</div>
            <div class="font-bold">
              {{
                manualScheduler.last_sync
                  ? formatDate(manualScheduler.last_sync, true)
                  : '-'
              }}
            </div>
          </div>

          <Message
            v-if="manualScheduler.last_error"
            severity="error"
            :closable="false"
            class="mb-4 sync-error-message"
          >
            <div>
              <div class="font-bold mb-1">Sync gagal</div>

              <div>
                {{ manualScheduler.last_error }}
              </div>
            </div>
          </Message>

          <Divider />

          <div class="field mb-3">
            <label class="block mb-2">Start Date</label>

            <Calendar
              v-model="manualForm.start_date"
              showTime
              hourFormat="24"
              class="w-full"
            />
          </div>

          <div class="field mb-4">
            <label class="block mb-2">End Date</label>

            <Calendar
              v-model="manualForm.end_date"
              :minDate="manualForm.start_date"
              showTime
              hourFormat="24"
              class="w-full"
            />
          </div>

          <div class="field mb-4">
            <label class="block mb-2">Max Sync</label>

            <InputNumber
              v-model="manualForm.limit"
              class="w-full"
              :useGrouping="false"
              :min="1"
              :max="100000"
              :allowEmpty="false"
            />
          </div>

          <div class="flex gap-2">
            <Button
              :label="manualScheduler.is_running ? 'Syncing...' : 'Run Sync'"
              :icon="
                manualScheduler.is_running
                  ? 'pi pi-spin pi-spinner'
                  : 'pi pi-play'
              "
              class="flex-1 mr-1"
              :loading="isLoadingManual || manualScheduler.is_running"
              :disabled="manualScheduler.is_running"
              @click="runManualSync"
            />

            <Button
              label="Stop"
              icon="pi pi-stop"
              severity="danger"
              outlined
              :disabled="!manualScheduler.is_running"
              @click="stopManualSync"
            />
          </div>
        </div>
      </div>
    </div>

    <Dialog
      :header="'Edit Setting ' + key"
      v-model:visible="dialog"
      :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
      :style="{ width: '25vw' }"
      :modal="true"
      :maximizable="true"
    >
      <form-setting
        :item="form"
        :loading="isLoadingSave"
        :setting="setting"
        @save="onSaveData"
        @close="dialog = false"
      />
    </Dialog>

    <Dialog
      v-model:visible="dialogJob"
      header="Atur Scheduler"
      :style="{ width: '40vw' }"
      modal
    >
      <div class="formgrid grid">
        <div class="field col-12 md:col-4">
          <label>Start Date</label>
          <Calendar
            v-model="jobForm.start_date"
            :maxDate="today"
            showTime
            hourFormat="24"
            class="w-full"
          />
        </div>
        <div class="field col-12 md:col-4">
          <label>End Date</label>
          <Calendar
            v-model="jobForm.end_date"
            :minDate="jobForm.start_date"
            :maxDate="today"
            showTime
            hourFormat="24"
            class="w-full"
          />
        </div>
      </div>

      <template #footer>
        <Button
          label="Batal"
          icon="pi pi-times"
          @click="onCancel"
          class="p-button-text mr-2"
        />
        <Button
          label="Simpan"
          icon="pi pi-check"
          :loading="isLoadingJob"
          @click="saveJobConfig('on')"
        />
      </template>
    </Dialog>

    <Dialog
      header="Non Aktifkan Scheduler"
      v-model:visible="dialogJobOff"
      :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
      :style="{ width: '50vw' }"
      :modal="true"
    >
      <div class="confirmation-content">
        <span>Job Yang berjalan akan dihentikan. Proses ?</span>
      </div>
      <template #footer>
        <Button
          label="Batal"
          icon="pi pi-times"
          @click="onCancel"
          class="p-button-text mr-2"
        />
        <Button
          label="Proses"
          icon="pi pi-check"
          @click="saveJobConfig('off')"
          class="p-button-text p-button-danger"
        />
      </template>
    </Dialog>

    <hotkey :shortcuts="['D']" @triggered="onTriggerHotkey" />
  </div>
</template>

<script>
import Hotkey from '@/components/Hotkey'
import FormSetting from '@/components/master/FormSetting'
import errorHandler from '@/helpers/error-handler'
import { formatDate } from '@/helpers/index'
import JobService from '@/services/JobService'
import SettingService from '@/services/SettingService'
import dayjs from 'dayjs'
import { FilterMatchMode } from 'primevue/api'
import Password from 'primevue/password'

export default {
  name: 'Setting',
  components: {
    Password,
    FormSetting,
    Hotkey,
  },
  data() {
    return {
      today: new Date(),
      settingService: null,
      jobService: null,
      dialog: false,
      dialogJob: false,
      dialogJobOff: false,
      isLoadingItems: false,
      isLoadingSave: false,
      isLoadingJob: false,
      isLoadingDb: false,
      items: [],
      form: {},
      key: '',
      setting: [],
      totalRecords: 0,
      jobItems: [],
      dbItems: [],
      isRunning: false,
      isFetching: false,
      autoScheduler: {},
      manualScheduler: {},

      autoForm: {
        enabled: false,
        run_time: null,
        limit: 5000,
      },

      manualForm: {
        start_date: null,
        end_date: null,
        limit: 5000,
      },

      isLoadingAuto: false,
      isLoadingManual: false,
      jobForm: {
        action: null,
        start_date: null,
        end_date: null,
      },
      dbForm: {
        DB_HOST: '',
        DB_PORT: null,
        DB_USER: '',
        DB_PASSWORD: '',
        DB_NAME: '',
      },
      dbFormBackup: {
        DB_HOST: '',
        DB_PORT: null,
        DB_USER: '',
        DB_PASSWORD: '',
        DB_NAME: '',
      },
      isTestingDb: false,
      isEditingDbForm: false,
      isDbFormActive: false,
      options: {
        page: 1,
        rows: 10,
        sortField: '',
        sortOrder: '1',
      },
      containModesOption: [
        { label: 'Contains', value: FilterMatchMode.CONTAINS },
      ],
      equalModesOption: [{ label: 'Equals', value: FilterMatchMode.EQUALS }],
      dbFields: [
        { key: 'DB_HOST', label: 'Host', type: 'text' },
        { key: 'DB_PORT', label: 'Port', type: 'number' },
        { key: 'DB_NAME', label: 'Database', type: 'text' },
        { key: 'DB_USER', label: 'User', type: 'text' },
        { key: 'DB_PASSWORD', label: 'Password', type: 'password' },
      ],
    }
  },
  created() {
    this.settingService = new SettingService()
    this.jobService = new JobService()
  },
  async beforeUnmount() {
    clearInterval(this.interval)
  },
  async mounted() {
    this.loadData(true)
    this.refreshBackgroundData()
    this.startRefreshInterval()

    // Watch untuk pause/resume interval saat active/inactive
    this.$watch('isDbFormActive', (newVal) => {
      if (newVal) {
        // Active - pause interval
        this.stopRefreshInterval()
      } else {
        // Inactive - resume interval
        this.startRefreshInterval()
      }
    })
  },
  methods: {
    startRefreshInterval() {
      if (this.interval) return
      this.interval = setInterval(() => {
        this.loadData(false)
        this.refreshBackgroundData()
      }, 10000)
    },
    stopRefreshInterval() {
      if (this.interval) {
        clearInterval(this.interval)
        this.interval = null
      }
    },
    onRequestData(e) {
      this.options = e
      this.loadData(false)
    },
    async refreshBackgroundData() {
      // Skip refresh jika user sedang mengedit form DB atau form sedang active
      if (this.isEditingDbForm || this.isDbFormActive) {
        return
      }

      try {
        const dbRes = await this.jobService.getDB()

        if (dbRes.status === 1 && dbRes.data.db_conf) {
          this.dbForm = {
            ...this.dbForm,
            ...dbRes.data.db_conf,
          }
        }
      } catch (err) {
        console.warn('Background data refresh failed', err)
      }
    },
    async loadData(showLoading = true) {
      if (this.isFetching) return

      this.isFetching = true

      if (showLoading) {
        this.isLoadingItems = true
      }

      try {
        const page = this.options.page
        const perPage = this.options.rows
        const sortField = this.options.sortField
        const sortOrder = this.options.sortOrder

        const settingRes = await this.settingService.paginate(
          page,
          perPage,
          sortField,
          sortOrder
        )

        // SETTINGS
        const allItems = settingRes.data.map((el) => {
          if (el.type === 'datetime') {
            el.value = el.value ? new Date(el.value) : ''
          }

          return { ...el }
        })

        this.items = allItems.filter((el) => !el.key.startsWith('scheduler_'))
        this.jobItems = allItems

        const scheduler = allItems.find(
          (el) => el.key === 'scheduler_sync_sales'
        )

        if (scheduler) {
          let parsed = {}

          try {
            parsed = JSON.parse(scheduler.value)
          } catch (e) {
            console.log('Invalid scheduler JSON')
          }

          const finalData = {
            ...scheduler,
            ...parsed,
          }

          this.autoScheduler = finalData
          this.manualScheduler = finalData

          let runTimeDate = null

          if (finalData.auto?.run_time) {
            const [hour, minute] = finalData.auto.run_time.split(':')

            runTimeDate = new Date()
            runTimeDate.setHours(Number(hour))
            runTimeDate.setMinutes(Number(minute))
            runTimeDate.setSeconds(0)
            runTimeDate.setMilliseconds(0)
          }

          this.autoForm = {
            enabled: finalData.auto?.enabled || false,
            run_time: runTimeDate,
            limit: finalData.auto?.limit || 5000,
          }
        }

        this.totalRecords = settingRes.total
      } catch (err) {
        errorHandler(err, 'Load Setting', this)
      } finally {
        if (showLoading) {
          this.isLoadingItems = false
        }

        this.isFetching = false
      }
    },
    onSort(e) {
      this.options.value = e
      this.loadData(false)
    },
    onEditData(data) {
      this.form = Object.assign({}, data)
      this.key = data.key.toUpperCase()
      this.dialog = true
    },
    onSaveData(data) {
      this.isLoadingSave = true
      const item = {}
      item.value =
        data.type === 'datetime'
          ? dayjs(data.value).format('YYYY-MM-DD HH:mm:ss')
          : data.value
      this.settingService
        .update(data.id, item)
        .then((res) => {
          if (res.data.status === 1) {
            this.loadData(false)
            this.$toast.add({
              severity: 'success',
              summary: 'Data setting',
              detail: 'Data berhasil disimpan.',
              life: 3000,
            })
            this.dialog = false
            // location.reload()
          }
        })
        .catch((err) => {
          errorHandler(err, 'Data setting', this)
        })
        .finally(() => (this.isLoadingSave = false))
    },
    onToggleJob(data) {
      if (data.is_running) {
        this.dialogJobOff = true
      } else {
        this.jobForm = {
          start_date: data.start_date ? new Date(data.start_date) : null,
          end_date: data.end_date ? new Date(data.end_date) : null,
        }
        this.dialogJob = true
      }
    },
    onCancel() {
      this.dialogJob = false
    },
    async saveJobConfig(actions) {
      this.isLoadingJob = true
      try {
        let body = {}
        if (actions === 'on') {
          body = {
            action: 'on',
            start_date: dayjs(this.jobForm.start_date).format(
              'YYYY-MM-DDTHH:mm:ss'
            ),
            end_date: dayjs(this.jobForm.end_date).format(
              'YYYY-MM-DDTHH:mm:ss'
            ),
          }
        } else {
          body = { action: 'off' }
        }

        const res = await this.jobService.sync(body)

        if (res.data.status === 1) {
          await this.jobService.stop()
          const now = dayjs().format('YYYY-MM-DD HH:mm:ss')

          const settingValue = {
            is_running: actions === 'on',
            next_run: null,
            start_date: this.jobForm.start_date
              ? dayjs(this.jobForm.start_date).format('YYYY-MM-DD HH:mm:ss')
              : null,
            end_date: this.jobForm.end_date
              ? dayjs(this.jobForm.end_date).format('YYYY-MM-DD HH:mm:ss')
              : null,
            stop_flag: actions === 'off',
            last_sync: actions === 'off' ? now : null,
          }

          const scheduler = this.jobItems.find(
            (item) => item.key === 'scheduler_sync_sales'
          )

          if (scheduler) {
            await this.settingService.update(scheduler.id, {
              value: JSON.stringify(settingValue),
            })

            await this.loadData(false)
            this.$toast.add({
              severity: 'success',
              summary: 'Scheduler',
              detail: res.data.data.message,
              life: 3000,
            })
            this.dialogJob = false
            this.dialogJobOff = false
          }
        }
      } catch (err) {
        errorHandler(err, 'Scheduler', this)
      } finally {
        this.isLoadingJob = false
      }
    },
    async saveDbConfig() {
      this.isLoadingDb = true
      try {
        const res = await this.jobService.updateDB(this.dbForm)
        if (res.data.status === 1) {
          this.$toast.add({
            severity: 'success',
            summary: 'DB Config',
            detail: res.data.data.message,
            life: 3000,
          })
          this.isEditingDbForm = false
          this.isDbFormActive = false
        }
      } catch (err) {
        errorHandler(err, 'DB Config', this)
      } finally {
        this.isLoadingDb = false
      }
    },
    toggleDbFormActive() {
      if (this.isDbFormActive) {
        // Deactivate
        this.isDbFormActive = false
        this.isEditingDbForm = false
      } else {
        // Activate - backup current form values
        this.dbFormBackup = { ...this.dbForm }
        this.isDbFormActive = true
      }
    },
    async testDbConfig() {
      this.isTestingDb = true
      try {
        const res = await this.jobService.testDB()

        if (res.data && res.data.status === 1) {
          const msg = res.data.data?.message || 'Koneksi berhasil'
          this.$toast.add({
            severity: 'success',
            summary: 'Uji Koneksi',
            detail: msg,
            life: 4000,
          })
        } else {
          const msg = res.data?.message || 'Gagal uji koneksi'
          this.$toast.add({
            severity: 'error',
            summary: 'Uji Koneksi',
            detail: 'Tidak dapat terhubung ke database. ' + msg,
            life: 5000,
          })
        }
      } catch (err) {
        const msg =
          err.response?.data?.message || err.message || 'Gagal uji koneksi'
        this.$toast.add({
          severity: 'error',
          summary: 'Uji Koneksi',
          detail: 'Tidak dapat terhubung ke database. ' + msg,
          life: 5000,
        })
      } finally {
        this.isTestingDb = false
      }
    },
    onDbFormFocus() {
      this.isEditingDbForm = true
    },
    onDbFormBlur() {
      // Delay clearing flag to allow button clicks to register
      setTimeout(() => {
        this.isEditingDbForm = false
      }, 300)
    },
    async saveAutoScheduler() {
      this.isLoadingAuto = true

      try {
        const form = {
          enabled: this.autoForm.enabled,
          run_time: this.autoForm.run_time
            ? dayjs(this.autoForm.run_time).format('HH:mm')
            : null,
          limit: this.autoForm.limit,
        }

        const res = await this.jobService.updateAuto(form)

        if (res.data.status === 1) {
          this.$toast.add({
            severity: 'success',
            summary: 'Auto Scheduler',
            detail: 'Auto scheduler updated.',
            life: 3000,
          })

          this.loadData(false)
        }
      } catch (err) {
        console.log(err)
        errorHandler(err, 'Auto Scheduler', this)
      } finally {
        this.isLoadingAuto = false
      }
    },

    async runManualSync() {
      if (!this.manualForm.start_date || !this.manualForm.end_date) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Manual Sync',
          detail: 'Start Date dan End Date wajib diisi.',
          life: 4000,
        })
        return
      }

      if (this.manualForm.end_date < this.manualForm.start_date) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Manual Sync',
          detail: 'End Date tidak boleh lebih kecil dari Start Date.',
          life: 4000,
        })
        return
      }

      this.isLoadingManual = true

      try {
        const body = {
          start_date: this.manualForm.start_date
            ? dayjs(this.manualForm.start_date).format('YYYY-MM-DDTHH:mm:ss')
            : null,

          end_date: this.manualForm.end_date
            ? dayjs(this.manualForm.end_date).format('YYYY-MM-DDTHH:mm:ss')
            : null,

          limit: this.manualForm.limit,
        }

        const res = await this.jobService.sync(body)

        if (res.data.status === 1) {
          await this.loadData(false)

          this.$toast.add({
            severity: 'success',
            summary: 'Manual Sync',
            detail: 'Sync started.',
            life: 3000,
          })
        }
      } catch (err) {
        errorHandler(err, 'Manual Sync', this)
      } finally {
        this.isLoadingManual = false
      }
    },

    async stopManualSync() {
      try {
        await this.jobService.stop()

        this.$toast.add({
          severity: 'warn',
          summary: 'Manual Sync',
          detail: 'Stop requested.',
          life: 3000,
        })

        this.loadData(false)
      } catch (err) {
        errorHandler(err, 'Stop Sync', this)
      }
    },
    onTriggerHotkey(payload) {
      if (payload.keyString === 'D') {
        this.onAddData()
      }
    },
    formatDate(value, time = false) {
      return formatDate(value, time)
    },
  },
}
</script>

<style scoped>
.sync-error-message {
  font-size: 0.85rem;
  padding: 0.75rem;
}

.sync-error-message .font-bold {
  font-size: 0.9rem;
}
</style>
